import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtFullDate, fmtMonthDay, fmtShortDay } from "../lib/format";
import type { DayAZM } from "../lib/types";

interface DailyChartProps {
  rows: DayAZM[];
  height?: number;
  showWeekdayLabels?: boolean;
  showRollingAverage?: boolean;
}

export default function DailyChart({
  rows,
  height = 240,
  showWeekdayLabels = true,
  showRollingAverage = false,
}: DailyChartProps) {
  const data = rows.map((r, idx) => ({
    date: r.date,
    minutes: r.minutes,
    rolling: rollingAvg(rows, idx, 7),
  }));
  const isToday = (date: string) => date === rows.at(-1)?.date;
  const maxY = Math.max(10, ...rows.map((r) => r.minutes)) * 1.1;

  return (
    <div className="-mx-1">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid
            stroke="var(--grid)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={(v) =>
              showWeekdayLabels ? fmtShortDay(v).slice(0, 2) : fmtMonthDay(v)
            }
            interval={Math.max(0, Math.floor(rows.length / 10))}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
            domain={[0, maxY]}
            width={32}
          />
          <Tooltip
            cursor={{ fill: "rgba(120,120,120,0.08)" }}
            content={<DailyTooltip />}
          />
          <Bar dataKey="minutes" radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell
                key={d.date}
                fill={
                  isToday(d.date) ? "var(--accent)" : "var(--accent-soft)"
                }
                stroke={
                  isToday(d.date) ? "var(--accent)" : "var(--accent)"
                }
                strokeOpacity={isToday(d.date) ? 1 : 0.45}
              />
            ))}
          </Bar>
          {showRollingAverage && (
            <Line
              type="monotone"
              dataKey="rolling"
              stroke="var(--fg)"
              strokeOpacity={0.45}
              strokeWidth={1.5}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TooltipPayload {
  active?: boolean;
  payload?: { payload: { date: string; minutes: number; rolling: number } }[];
}

function DailyTooltip({ active, payload }: TooltipPayload) {
  if (!active || !payload?.length) return null;
  const { date, minutes, rolling } = payload[0].payload;
  return (
    <div className="card border-zinc-200 px-3 py-2 text-xs shadow-md dark:border-zinc-700">
      <div className="font-medium">{fmtFullDate(date)}</div>
      <div className="tnum mt-1 text-zinc-900 dark:text-zinc-100">
        <span className="font-semibold">{minutes}</span>
        <span className="ml-1 text-zinc-500">AZM</span>
      </div>
      {Number.isFinite(rolling) && rolling > 0 && (
        <div className="tnum text-zinc-500">
          7-day avg: {rolling.toFixed(0)}
        </div>
      )}
    </div>
  );
}

function rollingAvg(rows: DayAZM[], idx: number, window: number): number {
  const start = Math.max(0, idx - window + 1);
  const slice = rows.slice(start, idx + 1);
  if (!slice.length) return 0;
  return slice.reduce((s, r) => s + r.minutes, 0) / slice.length;
}

// We rely on Bar / BarChart shake-out elsewhere too; export them for tree-shaking clarity.
export { Bar, BarChart };
